import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { UserContext } from 'context/userContext';
import { useLucky6Access } from 'hooks/useLucky6Access';
import { useKenoAccess } from 'hooks/useKenoAccess';
import { ThemeMenuIcon } from 'components/ThemeMenuIcon/ThemeMenuIcon';
import { MobileMenuItemProps } from 'components/mobile/MobileMenuItem/MobileMenuItem';
import {
  KenoScene,
  LiveScene,
  LuckySixScene,
  RouletteScene,
  SlotScene,
  SportScene,
} from './HomeScenes';
import styles from './ClassicHome.module.css';

/*
 * Classic mobile home (owner, 2026-10-24): six animated cards for the main
 * sections, a row of small tools and, at the bottom (owner moved it there),
 * a rotating banner. The
 * header with the menu and the footer with the balance stay as they are.
 */

type Card = {
  key: string;
  label: string;
  /** Lines under the label; several take turns. */
  sub: string | string[];
  scene: ReactNode;
  go: () => void;
};

type Slide = {
  title: string;
  text: string;
  cta: string;
  art: ReactNode;
  go: () => void;
};

const SLIDE_MS = 4500;
const SUB_MS = 5000;

interface Props {
  tools: MobileMenuItemProps[];
}

export const ClassicHome = ({ tools }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loggedUser } = useContext(UserContext);
  const luckySix = useLucky6Access();

  // Lucky Six is for players who are signed in; a guest is sent to log in.
  const openLuckySix = () => navigate(loggedUser ? '/lucky-six' : '/login');
  const showLuckySix = !loggedUser || luckySix;
  // Keno likewise: everyone, unless staff switched it off for the player.
  const keno = useKenoAccess();
  const showKeno = !loggedUser || keno;

  const cards: Card[] = [
    {
      key: 'sport',
      label: t('sports_betting_label'),
      sub: t('home_sub_sport'),
      scene: <SportScene />,
      go: () => navigate('/sports-betting'),
    },
    {
      key: 'live',
      label: t('live_betting_label'),
      sub: t('home_sub_live'),
      scene: <LiveScene />,
      go: () => navigate('/live-betting'),
    },
    {
      key: 'casino',
      label: t('casino_games_label'),
      sub: t('home_sub_casino'),
      scene: <SlotScene />,
      go: () => navigate('/casino-games'),
    },
    {
      key: 'liveCasino',
      label: t('live_casino_label'),
      sub: t('home_sub_live_casino'),
      scene: <RouletteScene />,
      go: () => navigate('/live-casino'),
    },
    ...(showLuckySix
      ? [
          {
            key: 'lucky',
            label: t('lucky_six_label'),
            sub: [t('home_sub_lucky'), t('home_sub_lucky_jackpots')],
            scene: <LuckySixScene />,
            go: openLuckySix,
          },
        ]
      : []),
    ...(showKeno
      ? [
          {
            key: 'keno',
            label: t('keno_label'),
            sub: t('home_sub_keno'),
            scene: <KenoScene />,
            go: () => navigate(loggedUser ? '/keno' : '/login'),
          },
        ]
      : []),
  ];

  const slides: Slide[] = [
    ...(showLuckySix
      ? [
          {
            title: t('ad_lucky_title'),
            text: t('ad_lucky_text'),
            cta: t('ad_cta_play'),
            art: (
              <span className={styles.artBalls}>
                {[
                  [7, '#e53935'],
                  [18, '#43a047'],
                  [23, '#1e88e5'],
                  [31, '#8e24aa'],
                ].map(([n, c]) => (
                  <i key={n} style={{ backgroundColor: c as string }}>
                    {n}
                  </i>
                ))}
              </span>
            ),
            go: openLuckySix,
          },
        ]
      : []),
    {
      title: t('ad_combo_title'),
      text: t('ad_combo_text'),
      cta: t('ad_cta_bet'),
      art: <span className={styles.artBig}>1X2</span>,
      go: () => navigate('/sports-betting'),
    },
    {
      title: t('ad_live_title'),
      text: t('ad_live_text'),
      cta: t('ad_cta_live'),
      art: <span className={styles.artBig}>LIVE</span>,
      go: () => navigate('/live-betting'),
    },
    {
      title: t('ad_live_casino_title'),
      text: t('ad_live_casino_text'),
      cta: t('ad_cta_enter'),
      art: <span className={styles.artBig}>♠</span>,
      go: () => navigate('/live-casino'),
    },
  ];

  const [subTick, setSubTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setSubTick((x) => x + 1), SUB_MS);
    return () => window.clearInterval(id);
  }, []);

  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setSlide((s) => (s + 1) % slides.length),
      SLIDE_MS
    );
    return () => window.clearInterval(id);
  }, [slides.length, slide]);

  // A round arrow tells the player there is more below, on screens too short
  // for the whole page; it goes once the bottom is reached.
  const rootRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const update = () => {
      const root = rootRef.current;
      if (!root) return;
      const { scrollTop, clientHeight, scrollHeight } = scrollParent(root);
      setMore(scrollHeight - scrollTop - clientHeight > 24);
    };
    update();
    // Late content (Lucky Six, fonts) changes the height after mounting.
    const late = window.setTimeout(update, 1500);
    // Whatever scrolls (the window or a layout box), the event reaches the
    // document in the capture phase.
    document.addEventListener('scroll', update, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', update);
    return () => {
      window.clearTimeout(late);
      document.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
    };
  }, []);
  const scrollDown = () => {
    const root = rootRef.current;
    if (!root) return;
    const scroller = scrollParent(root);
    scroller.scrollBy({ top: scroller.clientHeight * 0.7, behavior: 'smooth' });
  };

  return (
    <div className={styles.home} ref={rootRef}>
      <div className={styles.cards}>
        {cards.map((c) => (
          <button
            type="button"
            key={c.key}
            className={styles.card}
            onClick={c.go}
          >
            <span className={styles.glow} />
            <span className={styles.stage}>{c.scene}</span>
            <span className={styles.cardLabel}>
              {c.label}
              {Array.isArray(c.sub) ? (
                <small key={subTick % c.sub.length} className={styles.subTurn}>
                  {c.sub[subTick % c.sub.length]}
                </small>
              ) : (
                <small>{c.sub}</small>
              )}
            </span>
          </button>
        ))}
      </div>

      <div className={styles.tools}>
        {tools.map((item, i) => (
          <button
            type="button"
            key={i}
            className={styles.tool}
            onClick={() =>
              item.destinationUrl
                ? item.external
                  ? window.open(item.destinationUrl, '_blank', 'noopener')
                  : navigate(item.destinationUrl)
                : item.onClickAction?.()
            }
          >
            <span className={styles.toolIcon}>
              <ThemeMenuIcon icon={item.iconKey} alt="" variant="inline" />
            </span>
            {item.label}
          </button>
        ))}
      </div>

      <div className={styles.ads}>
        <div
          className={styles.slides}
          style={{
            width: `${slides.length * 100}%`,
            transform: `translateX(-${(slide * 100) / slides.length}%)`,
          }}
        >
          {slides.map((s, i) => (
            <button
              type="button"
              key={i}
              className={styles.slide}
              style={{ width: `${100 / slides.length}%` }}
              onClick={s.go}
              tabIndex={i === slide ? 0 : -1}
            >
              <span className={styles.slideTitle}>{s.title}</span>
              <span className={styles.slideText}>{s.text}</span>
              <span className={styles.cta}>{s.cta}</span>
              <span className={styles.art}>{s.art}</span>
            </button>
          ))}
        </div>
        <div className={styles.dots}>
          {slides.map((_, i) => (
            <button
              type="button"
              key={i}
              aria-label={`${i + 1}`}
              className={classNames(i === slide && styles.dotOn)}
              onClick={() => setSlide(i)}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        className={classNames(styles.more, !more && styles.moreGone)}
        onClick={scrollDown}
        aria-label={t('home_scroll_more')}
        title={t('home_scroll_more')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>
  );
};

/** The element that scrolls the page around `el`. */
function scrollParent(el: HTMLElement): HTMLElement {
  let p = el.parentElement;
  while (p) {
    const { overflowY } = getComputedStyle(p);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      p.scrollHeight > p.clientHeight + 1
    ) {
      return p;
    }
    p = p.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.body;
}
